"use client"
import { useState, useEffect } from "react"
import { useNavigate } from "react-router-dom"
import Layout from "../../components/Layout"
import Ticker from "../../components/Ticker"
import PrayerRequestModal from "../../components/PrayerRequestModal"
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome"
import { faEdit, faUpload, faDownload, faPoundSign } from "@fortawesome/free-solid-svg-icons"

const Dashboard = () => {
  const [error, setError] = useState("")
  const [isMobile, setIsMobile] = useState(false)
  const [isTablet, setIsTablet] = useState(false)
  const [sidebarCollapsed, setSidebarCollapsed] = useState(false)
  const navigate = useNavigate()
  const [showPrayerModal, setShowPrayerModal] = useState(false)

  // Font Awesome icons replacing previous SVGs
  const iconStyle = {
    color: "#fff",
    margin: "0 auto",
    display: "table",
    fontSize: "45px",
    lineHeight: 1,
  }
  const PencilIcon = () => <FontAwesomeIcon icon={faEdit} style={iconStyle} />
  const UploadIcon = () => <FontAwesomeIcon icon={faUpload} style={iconStyle} />
  const DownloadIcon = () => <FontAwesomeIcon icon={faDownload} style={iconStyle} />
  const PaymentIcon = () => <FontAwesomeIcon icon={faPoundSign} style={iconStyle} />

  // Dashboard cards data
  const dashboardCards = [
    {
      title: "Online Prayer Requests",
  description: "Browse through prayer forms and make your submission online",
      icon: PencilIcon,
    },
    {
      title: "Upload Prayer Request Forms",
  description: "You can upload photos of handwritten prayer request forms by selecting this option",
      icon: UploadIcon,
    },
    {
      title: "Download Prayer Request Forms",
  description: "Download prayer request forms to print at home and complete by hand",
      icon: DownloadIcon,
    },
    {
      title: "Payments & Offerings",
  description: "Make additional payments & offerings to the temple to Shinnyo-en",
      icon: PaymentIcon,
    },
  ]

  useEffect(() => {
    const checkScreenSize = () => {
      const width = window.innerWidth
      setIsMobile(width <= 768)
      setIsTablet(width > 768 && width <= 1024)
    }

    checkScreenSize()
    window.addEventListener("resize", checkScreenSize)

    return () => {
      window.removeEventListener("resize", checkScreenSize)
    }
  }, [navigate])

  const breadcrumb = (
    <div style={{ display: "flex", alignItems: "center", gap: "8px", color: "#6b7280", fontSize: "0.9rem" }}>
      <svg 
        width="20" 
        height="20" 
        viewBox="0 0 24 24" 
        fill="currentColor"
        style={{ cursor: "pointer" }}
        onClick={() => navigate("/home")}
      >
        <path d="M10,20V14H14V20H19V12H22L12,3L2,12H5V20H10Z" />
      </svg>
      <span>/</span>
      <span style={{ cursor: "pointer" }} onClick={() => navigate("/home")}>Home</span>
    </div>
  )

  const getSidebarWidth = () => {
    if (isMobile) return "80%"
    return sidebarCollapsed ? "80px" : "250px"
  }

  const getMainContentMargin = () => {
    if (isMobile) return "0"
    return sidebarCollapsed ? "80px" : "250px"
  }

  const getMainContentWidth = () => {
    if (isMobile) return "100%"
    return sidebarCollapsed ? "calc(100% - 80px)" : "calc(100% - 250px)"
  }

  const styles = {
    container: {
      display: "flex",
      minHeight: "100vh",
      backgroundColor: "#f5f5f5",
      fontFamily: "Arial, sans-serif",
      position: "relative",
    },
    header: {
      position: "fixed",
      top: 0,
      left: 0,
      right: 0,
      height: "80px",
      backgroundColor: "#6b7280",
      display: "flex",
      alignItems: "center",
      justifyContent: "space-between",
      padding: "0 20px",
      zIndex: 1000,
      boxShadow: "0 2px 4px rgba(0,0,0,0.1)",
    },
    logo: {
      fontSize: "2rem",
      fontFamily: "cursive",
      color: "white",
      fontWeight: "300",
    },
    headerLeft: {
      display: "flex",
      alignItems: "center",
      gap: "20px",
    },
    headerRight: {
      display: "flex",
      alignItems: "center",
      gap: "15px",
    },
    menuButton: {
      background: "none",
      border: "none",
      color: "white",
      fontSize: "1.5rem",
      cursor: "pointer",
      display: "flex",
      alignItems: "center",
      padding: "8px",
    },
    goBackButton: {
      background: "none",
      border: "none",
      color: "white",
      display: "flex",
      alignItems: "center",
      gap: "8px",
      cursor: "pointer",
      fontSize: "1rem",
      padding: "8px 12px",
    },
    cartIcon: {
      position: "relative",
      color: "white",
      fontSize: "1.5rem",
      cursor: "pointer",
      padding: "8px",
    },
    cartBadge: {
      position: "absolute",
      top: "0px",
      right: "0px",
      backgroundColor: "#ef4444",
      color: "white",
      borderRadius: "50%",
      width: "20px",
      height: "20px",
      fontSize: "0.75rem",
      display: "flex",
      alignItems: "center",
      justifyContent: "center",
    },
    signOutButton: {
      backgroundColor: "#9ca3af",
      color: "white",
      border: "none",
      padding: "8px 16px",
      borderRadius: "4px",
      cursor: "pointer",
      fontSize: "0.9rem",
    },
    sidebar: {
      backgroundColor: "#7fb3a3",
      paddingTop: "80px",
      minHeight: "100vh",
      position: "fixed",
      width: getSidebarWidth(),
      left: isMobile ? "0" : "0",
      transition: "width 0.3s ease",
      zIndex: 999,
      overflowY: "auto",
      overflowX: "hidden",
    },
    overlay: {
      position: "fixed",
      top: 0,
      left: 0,
      right: 0,
      bottom: 0,
      backgroundColor: "rgba(0,0,0,0.5)",
      zIndex: 998,
      display: isMobile ? "block" : "none",
    },
    menuItem: {
      display: "flex",
      alignItems: "center",
      justifyContent: sidebarCollapsed && !isMobile ? "center" : "space-between",
      padding: sidebarCollapsed && !isMobile ? "15px 10px" : "15px 20px",
      color: "white",
      cursor: "pointer",
      borderBottom: "1px solid rgba(255,255,255,0.1)",
      fontSize: "0.95rem",
      transition: "all 0.2s",
      borderTopLeftRadius: "30px",
      borderBottomLeftRadius: "30px",
      marginBottom: "20px",
      position: "relative",
    },
    menuItemActive: {
      backgroundColor: "#4b5563",
    },
    menuItemLeft: {
      display: "flex",
      alignItems: "center",
      gap: "12px",
      justifyContent: sidebarCollapsed && !isMobile ? "center" : "flex-start",
      width: "100%",
    },
    menuItemText: {
      display: sidebarCollapsed && !isMobile ? "none" : "block",
      whiteSpace: "nowrap",
      overflow: "hidden",
      textOverflow: "ellipsis",
    },
    menuItemIcon: {
      fontSize: "1.2rem",
      minWidth: "20px",
      display: "flex",
      alignItems: "center",
      justifyContent: "center",
    },
    mainContent: {
      marginLeft: getMainContentMargin(),
      paddingTop: "80px",
      flex: 1,
      padding: isMobile ? "20px" : "30px",
      width: getMainContentWidth(),
      transition: "margin-left 0.3s ease, width 0.3s ease",
    },
    breadcrumb: {
      display: "flex",
      alignItems: "center",
      marginBottom: "30px",
      color: "#6b7280",
      fontSize: "0.9rem",
      gap: "8px",
    },
    cardsGrid: {
      display: "grid",
      gridTemplateColumns: isMobile ? "1fr" : "repeat(2, 1fr)",
      gap: "20px",
      width: "100%",
      marginTop: "30px",
    },
    card: {
      backgroundColor: "#89b0ae",
      borderRadius: "12px",
      padding: "10px",
      textAlign: "center",
      color: "white",
      cursor: "pointer",
      display: "flex",
      flexDirection: "column",
      alignItems: "center",
      justifyContent: "center",
      minHeight: "200px",
      width: "90%",
      margin: "0 auto",
      marginTop: "20px",
      transition: "all 0.3s ease",
  border: "4px solid transparent", // reserve space to avoid layout shift on hover
    },
    cardIcon: {
      fontSize: "3rem",
      marginBottom: "0px",
      opacity: 0.9,
      display: "flex",
      justifyContent: "center",
    },
    cardTitle: {
      fontSize: "1.3rem",
      fontWeight: "600",
      marginBottom: "15px",
      lineHeight: "1.3",
    },
    cardDescription: {
      fontSize: "0.95rem",
      opacity: 0.9,
      lineHeight: "1.4",
    },
    error: {
      color: "#ef4444",
      fontSize: "0.9rem",
      textAlign: "center",
      marginBottom: "20px",
    },
  }

  return (
    <Layout
      activeMenuItem="Home"
      breadcrumb={breadcrumb}
      sidebarCollapsed={sidebarCollapsed}
      setSidebarCollapsed={setSidebarCollapsed}
    >
      {error && <p style={styles.error}>{error}</p>}
      <Ticker />
      <div style={styles.cardsGrid}>
        {dashboardCards.map((card, index) => {
          const IconComponent = card.icon
          return (
            <div
              key={index}
              style={styles.card}
              onMouseEnter={(e) => {
                e.currentTarget.style.transform = "translateY(-5px)"
                e.currentTarget.style.boxShadow = "0 10px 25px rgba(0,0,0,0.15)"
                e.currentTarget.style.backgroundColor = "#555B6E"
                e.currentTarget.style.border = "4px solid #FFD6BA"
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.transform = "translateY(0)"
                e.currentTarget.style.boxShadow = "none"
                e.currentTarget.style.backgroundColor = "#89b0ae"
                e.currentTarget.style.border = "4px solid transparent"
              }}
              onClick={() => {
                if (card.title === "Online Prayer Requests") {
                  setShowPrayerModal(true)
                } else if (card.title === "Upload Prayer Request Forms") {
                  navigate("/upload")
                } else if (card.title === "Download Prayer Request Forms") {
                  navigate("/download-forms")
                } else if (card.title === "Payments & Offerings") {
                  navigate("/offerings")
                }
              }}
            >
              <div style={styles.cardIcon}>
                <IconComponent />
              </div>
              <h3 style={styles.cardTitle}>{card.title}</h3>
              <p style={styles.cardDescription}>{card.description}</p>
            </div>
          )
        })}
      </div>

      <PrayerRequestModal 
        isOpen={showPrayerModal} 
        onClose={() => setShowPrayerModal(false)} 
      />
    </Layout>
  )
}

export default Dashboard
